/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  accountsSelector,
  globalGroupDecendantAccountIdsSelector,
  globalGroupIdSelector
} from '../../store/selectors/global-group-chooser-selectors';
import {getImmediateSubAccountIds, isImmediateSubAccountsOnly} from '../../store/subAccounts';

// prevents a new reference (and an options reload) while the hierarchy is pending ...
const PENDING_ACCOUNT_IDS: number[] = [];

/**
 * The useGeofenceDescendantIds hook returns the descendant account ids used by
 * the Geofence Filter, limited to the global group's immediate sub-accounts for
 * accounts flagged by `isImmediateSubAccountsOnly`.
 *
 * While the global group changes, globalGroupId is updated before the group's
 * account hierarchy - no account ids are returned until both match, so the
 * filter never loads geofences for the previous group's accounts.
 *
 * ```typescript
 *  const descendantAccountId = useGeofenceDescendantIds();
 * ```
 */
export const useGeofenceDescendantIds = (): number[] => {
  const descendantAccountIds = useSelector(globalGroupDecendantAccountIdsSelector);
  const globalGroupId        = useSelector(globalGroupIdSelector);
  const accounts             = useSelector(accountsSelector);

  return useMemo(() => {
    let accountIds = PENDING_ACCOUNT_IDS;
    if (globalGroupId && entityId(accounts?.[0]) === globalGroupId) {
      accountIds = isImmediateSubAccountsOnly(globalGroupId)
        ? getImmediateSubAccountIds(accounts[0], globalGroupId)
        : descendantAccountIds;
    }
    return accountIds;
  }, [descendantAccountIds, accounts, globalGroupId]);
};
